/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useState} from "react"
import styles from "./StageFunnel.module.css"
import {formatDecimal, handleError} from "helpers"
import {admissionService} from "services"
import {StageFunnelStatsItem} from "types/admissions"
import {Spin} from "antd"
import moment from "moment"
import {useModel} from "hooks"
import {FieldOperator, FilterKey} from "types/filter"
import {ONE_DAY_IN_SECONDS} from "data/constants"
import {STATES_OPTIONS} from "types/students"

type Props = {
    form: any
    setState: any
    departmentId: number
    goToStudentTab: () => void
}

export function StageFunnel(props: Props) {
    const {form, setState, departmentId, goToStudentTab} = props
    const [stats, setStats] = useState<StageFunnelStatsItem[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const model = useModel()

    useEffect(() => {
        if (form.applySearch) {
            getStatsInfo()
            setState({applySearch: false})
        }
    }, [form])

    useEffect(() => {
        getStatsInfo()
    }, [])

    function getFilter(form) {
        const {campus, program, advisor, term, dateRange, startDateRange} = form
        let filter: any = {}
        if (term) {
            filter.termsIds = term.map(({id}) => id)
        }
        if (campus) {
            filter.campusIds = campus.map(({id}) => id)
        }
        if (program) {
            filter.programIds = program.map(({id}) => id)
        }
        if (advisor) {
            filter.advisorProfileIds = (advisor || []).map((item) => item?.profileId).filter((item) => item)
        }
        if (dateRange) {
            filter.dateRange = [moment(dateRange[0]).format("YYYY-MM-DD"), moment(dateRange[1]).format("YYYY-MM-DD")]
        }
        if (startDateRange) {
            filter.startDateRange = [
                moment(startDateRange[0]).format("YYYY-MM-DD"),
                moment(startDateRange[1]).format("YYYY-MM-DD")
            ]
        }
        return filter
    }

    async function getStatsInfo() {
        try {
            setIsLoading(true)
            const filter = getFilter(props.form)
            const data = await admissionService.getStageFunnelStats({
                departmentId,
                advisorProfileIds: [],
                ...filter
            })

            setStats(data)
        } catch (error) {
            handleError(error)
        } finally {
            setIsLoading(false)
        }
    }

    const goToDetails = (statItem: StageFunnelStatsItem) => {
        let filters: any = {}
        filters.state = {
            operator: FieldOperator.Equal,
            value: STATES_OPTIONS.filter((x) => x.id === statItem.stageIdStr)
        }
        if (form.dateRange) {
            filters.createdAtRange = {
                operator: FieldOperator.Between,
                value: [moment(form.dateRange[0]), moment(form.dateRange[1])]
            }
        }
        if (form.startDateRange) {
            filters.startDateRange = {
                operator: FieldOperator.Between,
                value: [moment(form.startDateRange[0]), moment(form.startDateRange[1])]
            }
        }
        ;["campus", "program"].forEach((key) => {
            if (form[key]) {
                filters[key] = {
                    operator: FieldOperator.Equal,
                    value: form[key]
                }
            }
        })
        if (form.advisor) {
            filters.admAdvisors = {
                operator: FieldOperator.Equal,
                value: form.advisor
            }
        }
        if (form.term) {
            filters.startingTerm = {
                operator: FieldOperator.Equal,
                value: form.term
            }
        }

        model.updateStorageFilter(FilterKey.AdmissionPage_Students, {
            filters
        })
        goToStudentTab()
    }

    return (
        <div className={styles.wrap}>
            <p className={styles.title}>Conversion between stages (states)</p>
            {isLoading ? (
                <Spin className={styles.loading} />
            ) : (
                <div className={styles.body}>
                    <div className={styles.headerWrap}>
                        <p className={styles.headerTarget}>Avg Days</p>
                        <p className={styles.headerTarget}>Current</p>
                    </div>
                    <div className={styles.itemWrap}>
                        {stats.map((x, index) => (
                            <ProgressBar key={x.stageId} statItem={x} index={index} goToDetails={goToDetails} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

type ProgressBarProps = {
    statItem: StageFunnelStatsItem
    index: number
    goToDetails: (statItem: StageFunnelStatsItem) => void
}

function ProgressBar(props: ProgressBarProps) {
    const {statItem, index, goToDetails} = props
    const avgDays =
        statItem.avgSeconds !== null
            ? statItem.avgSeconds > 0
                ? formatDecimal(statItem.avgSeconds / ONE_DAY_IN_SECONDS)
                : 0
            : null
    return (
        <div className={styles.item}>
            <div className={styles.circle} style={{background: `var(--primary-400-base)`}}></div>
            <p className={styles.item__title}>{statItem.name}</p>
            <p className={styles.percent}>{index > 0 ? `${statItem.conv}%` : ""}</p>
            <p className={styles.currentValue} style={{background: "var(--black-300)"}}>
                {statItem.totalUsers}
            </p>
            <div className={styles.progressWrap}>
                <div
                    className={styles.progressValue}
                    style={{
                        width: `${statItem.conv > 100 ? 100 : statItem.conv}%`,
                        background: `var(--primary-400-base)`
                    }}></div>
            </div>
            <div className={styles.targetValue}>{avgDays}</div>
            <div className={styles.targetValue} onClick={() => goToDetails(statItem)}>
                {statItem.currentTotal}
            </div>
        </div>
    )
}
